// Comma-format an integer string using regex — no Number() roundtrip,
// so 22+ digit values keep their exact form.
const formatLongIntegerString = (digits: string): string =>
  digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

export const formatNumberWithCommas = (value: string | number): string => {
  if (value === null || value === undefined || value === '') return '';

  const raw = String(value).replace(/,/g, '');

  // Any alphabet → treat as free text
  if (/[a-zA-Z]/.test(raw)) {
    return raw;
  }

  // Numeric only (allow decimal)
  if (!/^\d*\.?\d*$/.test(raw)) {
    return raw;
  }

  // Integers go through the regex formatter — no Number() roundtrip,
  // so values past Number.MAX_SAFE_INTEGER keep their exact form. The
  // bug this fixes (TCX-356): Number('9999999999999999') rounds to
  // 1e16, and `toLocaleString` flips to scientific notation for
  // anything ≥ 1e21. The CashRequiredInput controlled-input pipeline
  // then re-renders with the scientific form on every keystroke, so
  // backspace can't make progress — the value snaps back to the
  // precision-lost form. The string formatter avoids both issues.
  // codex review caught the original boundary at 16-digit values
  // above MAX_SAFE_INTEGER (e.g. '9999999999999999').
  if (!raw.includes('.')) {
    return formatLongIntegerString(raw);
  }

  // TCX-867 review (P1) — comma-format the integer part with the same
  // no-Number()-roundtrip formatter as above, and leave the fractional part
  // untouched. The previous `Number(raw).toLocaleString()` path: (1) deleted
  // an in-progress trailing dot ('1500.' -> 1500 -> '1,500', so the next
  // digit typed lands on the wrong side of the decimal); (2) rounded past 3
  // fraction digits ('1500.5678' -> '1,500.568'); (3) dropped a trailing
  // zero ('1500.50' -> '1,500.5'); (4) lost precision on very small/large
  // decimals for the same reason TCX-356 fixed the integer path above.
  const [intPart, fracPart] = raw.split('.');
  return `${formatLongIntegerString(intPart)}.${fracPart}`;
};

export const stripCommas = (v: unknown): string =>
  v === null || v === undefined ? '' : String(v).replace(/,/g, '');
